/**
 * @file     submitLead.ts
 * @readme   ./CLAUDE.md
 * @purpose  The single client-side entrypoint for submitting a lead to /api/leads.
 *           Wraps secureFetch + fires the tiered conversion event on success.
 *           All lead-capturing forms must import this instead of calling secureFetch directly.
 */
'use client';

import { gatedSecureFetch } from '@/services/phone-gate/gatedFetch';
import { trackLeadConversion, trackMetaLeadConversion } from '@/services/analytics/leadConversion';
import type { LeadType } from './leadTypes';
import { willBrowserEmitGa4 } from '@/services/analytics/gtagDelivery';
import { browserGpcEnabled } from '@/services/analytics/gpc';
import { analyticsConsentDenied, CONSENT_COOKIE } from '@/services/analytics/consent';
import { parseJsonCookie } from '@/services/analytics/attributionCookies';
import { smsConsentPayload } from '@/core/config/smsConsent';

export interface SubmitLeadResult {
  ok: boolean;
  status: number;
  referenceId?: string;
  error?: string;
  /** Raw response JSON if the caller needs anything beyond success/referenceId. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

export interface SubmitLeadOptions {
  /** Page path where the form lives, used for analytics attribution. */
  sourcePage?: string;
  /** Flow/form identifier for analytics attribution (e.g. 'sellerFlow'). */
  flowId?: string;
  /** If true (default), fires the conversion event on success.
   *  Set to false for internal test forms or duplicate submissions that shouldn't double-count. */
  trackConversion?: boolean;
  /** Landing page attribution carried from /l/[slug]. Flows to FUB + GA4 + Ads. */
  landingSlug?: string;
  /** Experiment sub-variant id chosen on the landing page. */
  landingVariant?: string;
  /** Landing-page intent classification. */
  landingIntent?: string;
  /** BCP-47 locale the page was viewed in. CRM tracks this; backend stays English. */
  landingLocale?: string;
}

const RELTA_CID_COOKIE = 'relta_cid';
const RELTA_CID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function createGaClientId(): string {
  const randomPart =
    typeof crypto !== 'undefined' && 'getRandomValues' in crypto
      ? crypto.getRandomValues(new Uint32Array(1))[0]
      : Math.floor(Math.random() * 1e10);
  return `${randomPart}.${Math.floor(Date.now() / 1000)}`;
}

/**
 * Campaign parameters for this submission: whatever is on the current URL,
 * falling back to the first-touch cookie. `body.utm` is threaded to every FUB
 * submitter and rendered in every note, and was null on all 153 leads captured
 * before this because nothing ever populated it.
 */
function readCampaignParams(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const current = new URLSearchParams(window.location.search);
  const live: Record<string, string> = {};
  for (const key of keys) {
    const value = current.get(key);
    if (value) live[key] = value;
  }
  if (Object.keys(live).length > 0) return live;

  const parsed = parseJsonCookie<Record<string, string>>(readCookie('relta_utm_ft'));
  return parsed && Object.keys(parsed).length > 0 ? parsed : undefined;
}

function ensureReltaClientId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  // Same GPC promise as GtagBoot: no first-party GA id is written or sent, and
  // the server Measurement Protocol leg is skipped on Sec-GPC.
  if (browserGpcEnabled() || analyticsConsentDenied(readCookie(CONSENT_COOKIE))) return undefined;
  const existing = readCookie(RELTA_CID_COOKIE);
  if (existing) return existing;

  const next = createGaClientId();
  document.cookie = `${RELTA_CID_COOKIE}=${encodeURIComponent(next)}; Max-Age=${RELTA_CID_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
  return readCookie(RELTA_CID_COOKIE) ?? next;
}

/**
 * POST a lead payload to /api/leads as JSON, and fire the tiered Google Ads /
 * GA4 conversion on a 2xx response. Analytics failures never surface to the caller.
 *
 * Callers should pass the lead `type` at the top of `payload`, it's the only field
 * we inspect here for routing conversion tiers.
 *
 * @example
 *   const res = await submitLead(
 *     { type: 'seller', name, email, phone, address, ... },
 *     { sourcePage, flowId: flowConfig.flowId },
 *   );
 *   if (!res.ok) { showError(res.error); return; }
 */
export async function submitLead(
  payload: { type: LeadType } & Record<string, unknown>,
  options: SubmitLeadOptions = {},
): Promise<SubmitLeadResult> {
  // Agent mode: an admin keying in a client's lead is not an ad conversion, so
  // no GA4, Ads or Meta leg fires, and 'none' tells the server to skip its leg too.
  const trackConversion = options.trackConversion !== false && payload.agentSubmission !== true;

  // Read the first-touch Google Ads click id cookie (set in GtagBoot when
  // the user arrives with ?gclid= / ?gbraid= / ?wbraid=) so the server can attach it
  // to the lead record + forward via the GA4 Measurement Protocol.
  let firstTouchGclid: string | undefined;
  let ga4ClientId: string | undefined;
  // Which leg reports this conversion to GA4. Resolved BEFORE the POST so the
  // route handler knows whether to fire its Measurement Protocol backstop:
  // GA4 does not deduplicate `generate_lead`, so two legs mean two conversions.
  const conversionLeg: 'client' | 'server' | 'none' = !trackConversion
    ? 'none'
    : (await willBrowserEmitGa4())
      ? 'client'
      : 'server';
  if (typeof document !== 'undefined') {
    firstTouchGclid = readCookie('relta_gclid');

    // `relta_cid` is our own first-party GA client id, set eagerly by
    // GtagBoot and also fed to gtag's config as `client_id`. This
    // helper creates it here as a backstop for fast conversions that happen
    // before the lazy analytics script runs, so the server MP event and client
    // gtag event share the same GA4 dedupe key.
    ga4ClientId = ensureReltaClientId();
  }

  let response: Response;
  try {
    response = await gatedSecureFetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // SMS consent only from the form's ticked ConsentChoices SMS box (`smsOptIn`).
        ...(payload.agentSubmission === true ? {} : smsConsentPayload(payload.phone, payload.smsOptIn === true)),
        ...payload,
        utm: payload.utm ?? readCampaignParams(),
        firstTouchGclid,
        ga4ClientId,
        ga4ConversionLeg: conversionLeg,
        landingSlug: options.landingSlug,
        landingVariant: options.landingVariant,
        landingIntent: options.landingIntent,
        landingLocale: options.landingLocale,
      }),
    });
  } catch (err) {
    trackLeadOutcome(payload, false, 'network_error');
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any = null;
  try { data = await response.json(); } catch { /* empty body */ }

  if (!response.ok) {
    trackLeadOutcome(payload, false, `http_${response.status}`);
    return {
      ok: false,
      status: response.status,
      error: data?.error ?? `Request failed (${response.status})`,
      data,
    };
  }

  const referenceId: string | undefined = data?.referenceId;

  trackLeadOutcome(payload, true);

  // GA4: only the leg the server was told about emits. When gtag cannot deliver
  // (ad blocker, offline, or no measurement id), the route handler's Measurement
  // Protocol leg covers GA4. Meta has no server leg, so the browser still reports Lead.
  const conversion = {
    flowId: options.flowId,
    sourcePage: options.sourcePage,
    referenceId,
    landingSlug: options.landingSlug,
    landingVariant: options.landingVariant,
    landingIntent: options.landingIntent,
    landingLocale: options.landingLocale,
  };
  if (conversionLeg === 'client') {
    trackLeadConversion(payload.type, conversion);
  } else if (conversionLeg === 'server') {
    trackMetaLeadConversion(payload.type, conversion);
  }

  return { ok: true, status: response.status, referenceId, data };
}

/**
 * Record the conversion on the behavioural spine. Without this there is no
 * outcome event in `analytics_events` at all: 4,673 listing views and 1,503
 * searches sat there with nothing to divide by, so no funnel, conversion rate,
 * or time-to-convert was computable from our own data.
 */
function trackLeadOutcome(
  payload: { type: LeadType } & Record<string, unknown>,
  ok: boolean,
  reason?: string,
): void {
  if (typeof window === 'undefined') return;
  void import('@/services/analytics/relta/client')
    .then(({ reltaAnalytics }) => {
      if (ok) {
        reltaAnalytics.track('lead.submit', {
          lead_type: String(payload.type),
          has_email: Boolean(payload.email),
          has_phone: Boolean(payload.phone),
          ...(typeof payload.intent === 'string' ? { intent: payload.intent } : {}),
        });
      } else {
        reltaAnalytics.track('lead.submit.fail', {
          lead_type: String(payload.type),
          ...(reason ? { reason } : {}),
        });
      }
    })
    .catch(() => { /* analytics never blocks a submission result */ });
}
